import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { FlexColumn, FlexRow } from '../../components/atoms/layout/Flex'
import usePage from '../../hooks/usePage'
import Button from '../../components/atoms/inputs/Button'
import MoneyspaceSelectDialog from '../../components/organisms/moneyspace/MoneyspaceSelectDialog'
import { useTransactionCreateDialog } from '../../hooks/useDialog'
import { CompanyActions } from '../../store/company'
import { CompanySelectors } from '../../store/company/selectors'
import Table from '../../components/atoms/display/Table'
import TableRow from '../../components/atoms/display/TableRow'
import TableCell from '../../components/atoms/display/TableCell'
import Label from '../../components/atoms/display/Label'
import { projectStatus } from '../../types/construction/project'
import TransactionFavoriteButton from '../../components/organisms/transaction/TransactionFavoriteButton'
import TransactionAmountView from '../../components/molecules/display/TransactionAmountView'
import { Transaction, TransactionPhaseBilling, TransactionPhaseBillingDetail } from '../../types/transaction'
import TransactionStatusLabel from '../../components/molecules/display/TransactionStatusLabel'
import { TransactionActions } from '../../store/transactions'
import useDay from '../../hooks/useDay'
import useConsts from '../../hooks/useConsts'
import useRouter from '../../hooks/useRouter'

export default function ProjectPage() {
  const page = usePage()
  const { projectId } = useParams()
  const [openMoneyspaceSelectDialog, setOpenMoneyspaceSelectDialog] = useState(false)
  const contractTransactionDialog = useTransactionCreateDialog()
  const dispatch = useDispatch()
  const detail = useSelector(CompanySelectors.projectDetail)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const { formatDate } = useDay()
  const { phaseName } = useConsts()
  const { transaction: transactionPage } = useRouter()

  useEffect(() => {
    page.changeTitle('取引作成')
  }, [page])

  useEffect(() => {
    if (projectId) {
      dispatch(CompanyActions.fetchProjectDetail({ projectId }))
    }
  }, [dispatch, projectId])

  useEffect(() => {
    if (detail) {
      dispatch(
        TransactionActions.loadTransactions({
          transactionIds: detail.orders.map((order) => order.transactionId),
          onLoad: setTransactions,
        })
      )
    }
  }, [dispatch, detail, setTransactions])

  const handleSelectMoneyspace = useCallback(
    (moneyspaceId: string) => {
      contractTransactionDialog.open(moneyspaceId, projectId, true)
    },
    [contractTransactionDialog, projectId]
  )

  if (!detail) {
    return null
  }

  return (
    <FlexColumn>
      <Label variant="subtitle" text={detail.project.name} />
      <Table>
        <TableRow>
          <TableCell color="header" size="sm3">
            案件種別
          </TableCell>
          <TableCell size="sm">
            <Label text={detail.project.constructionType ?? ''} />
          </TableCell>
          <TableCell color="header" size="sm3">
            ステータス
          </TableCell>
          <TableCell size="sm">
            {detail.project.status ? <Label text={projectStatus[detail.project.status]} /> : <Label text="不明" />}
          </TableCell>
        </TableRow>
      </Table>
      <Button onClick={() => setOpenMoneyspaceSelectDialog(true)}>取引作成</Button>
      <MoneyspaceSelectDialog
        title="取引を作成するマネースペースの選択"
        open={openMoneyspaceSelectDialog}
        onSelect={handleSelectMoneyspace}
        onClose={() => setOpenMoneyspaceSelectDialog(false)}
      />
      <Table
        header={
          <TableRow>
            <TableCell header>取引名</TableCell>
            <TableCell header>現場住所</TableCell>
            <TableCell header size="sm2">
              金額
            </TableCell>
            <TableCell header size="xs">
              担当者
            </TableCell>
            <TableCell header size="xs">
              締め日
            </TableCell>
            <TableCell header size="xs">
              支払日
            </TableCell>
            <TableCell header size="xs">
              フェーズ
            </TableCell>
            <TableCell header size="xs">
              ステータス
            </TableCell>
          </TableRow>
        }
      >
        {transactions.map((transaction) => (
          <TableRow key={transaction.id} onClick={() => transactionPage(transaction.id)}>
            <TableCell>
              <FlexRow align="center">
                <TransactionFavoriteButton transaction={transaction} />
                {transaction.name}
              </FlexRow>
            </TableCell>
            <TableCell>{transaction.siteAddress}</TableCell>
            <TableCell size="sm2" align="right">
              <TransactionAmountView transaction={transaction} />
            </TableCell>
            <TableCell size="sm3" align="center">
              <FlexRow justify="center" wrap>
                {transaction.pic && <Label text={transaction.pic.name} />}
                {transaction.picSub && <Label text={transaction.picSub.name} />}
                {transaction.optional?.rsUserName && <Label text={transaction.optional.rsUserName} />}
                {!transaction.pic && !transaction.picSub && !transaction.optional?.rsUserName && <Label text="-" />}
              </FlexRow>
            </TableCell>
            <TableCell size="xs" align="center">
              {[TransactionPhaseBillingDetail, TransactionPhaseBilling].includes(transaction.currentPhase) ? (
                <Label text={transaction.closingDate ? formatDate(transaction.closingDate) : '-'} />
              ) : (
                <Label text="-" />
              )}
            </TableCell>
            <TableCell size="xs" align="center">
              {[TransactionPhaseBillingDetail, TransactionPhaseBilling].includes(transaction.currentPhase) ? (
                <Label text={transaction.paymentDate ? formatDate(transaction.paymentDate) : '-'} />
              ) : (
                <Label text="-" />
              )}
            </TableCell>
            <TableCell size="xs" align="center">
              {phaseName(transaction.currentPhase)}
            </TableCell>
            <TableCell size="xs" align="center">
              <TransactionStatusLabel transaction={transaction} />
            </TableCell>
          </TableRow>
        ))}
      </Table>
    </FlexColumn>
  )
}
